import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useUnitSchedule = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_schedule", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_schedule")
        .select("*")
        .eq("project_id", projectId!)
        .order("sort_order")
        .order("unit_number");
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useUnitScopeItems = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_scope_items", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_scope_items")
        .select("*")
        .eq("project_id", projectId!);
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useUnitAssignments = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_assignments", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_assignments")
        .select("*, vendors(name), work_orders(number)")
        .eq("project_id", projectId!);
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useBillingCycles = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["billing_cycles", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_cycles")
        .select("*")
        .eq("project_id", projectId!)
        .order("cycle_number", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useUnitCompletions = (projectId: string | undefined, cycleId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_completions", projectId, cycleId],
    queryFn: async () => {
      let q = supabase
        .from("unit_completions")
        .select("*, unit_schedule(unit_number), vendors(name)")
        .eq("project_id", projectId!);
      if (cycleId) q = q.eq("billing_cycle_id", cycleId);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useUnitCompletionDiscussions = (completionId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_completion_discussions", completionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_completion_discussions")
        .select("*")
        .eq("unit_completion_id", completionId!)
        .order("created_at");
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!completionId,
  });
};

export const useImportUnits = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      companyId: string;
      clearExisting?: boolean;
      units: Array<{ unit_number: string; floor_number?: string; building?: string; unit_type?: string }>;
      scopeItems: Array<{ unit_number: string; sov_line_id: string; quantity: number; has_scope: boolean }>;
    }) => {
      // If re-importing, clear existing data first (scope items before units due to FK)
      if (input.clearExisting) {
        await supabase
          .from("unit_scope_items")
          .delete()
          .eq("project_id", input.projectId);
        await supabase
          .from("unit_schedule")
          .delete()
          .eq("project_id", input.projectId);
      }

      // Upsert units (prevents duplicates if double-fired)
      const unitRows = input.units.map((u, i) => ({
        company_id: input.companyId,
        project_id: input.projectId,
        unit_number: u.unit_number,
        floor_number: u.floor_number || null,
        building: u.building || null,
        unit_type: u.unit_type || null,
        sort_order: i,
      }));

      const { data: insertedUnits, error: uErr } = await supabase
        .from("unit_schedule")
        .upsert(unitRows as any, { onConflict: "project_id,unit_number", ignoreDuplicates: false })
        .select("id, unit_number");
      if (uErr) throw uErr;

      const unitMap = new Map(insertedUnits.map((u: any) => [u.unit_number, u.id]));

      // Upsert scope items (prevents duplicates if double-fired)
      const scopeRows = input.scopeItems
        .filter(si => unitMap.has(si.unit_number))
        .map(si => ({
          company_id: input.companyId,
          unit_id: unitMap.get(si.unit_number)!,
          project_id: input.projectId,
          sov_line_id: si.sov_line_id,
          has_scope: si.has_scope,
          quantity: si.quantity,
        }));

      if (scopeRows.length > 0) {
        const { error: siErr } = await supabase
          .from("unit_scope_items")
          .upsert(scopeRows as any, { onConflict: "unit_id,sov_line_id", ignoreDuplicates: false });
        if (siErr) throw siErr;
      }

      return { unitCount: insertedUnits.length, scopeCount: scopeRows.length };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["unit_schedule", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["unit_scope_items", vars.projectId] });
    },
  });
};

export const useCreateBillingCycle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      companyId: string;
      projectId: string;
      cycleName: string;
      cycleNumber: number;
      cutoffDate: string;
    }) => {
      const { data, error } = await supabase
        .from("billing_cycles")
        .insert({
          company_id: input.companyId,
          project_id: input.projectId,
          cycle_name: input.cycleName,
          cycle_number: input.cycleNumber,
          cutoff_date: input.cutoffDate,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["billing_cycles", vars.projectId] });
    },
  });
};

export const useAssignUnits = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      companyId: string;
      projectId: string;
      assignments: Array<{
        unit_id: string;
        unit_scope_item_id: string;
        sov_line_id: string;
        work_order_id: string;
        vendor_id: string;
      }>;
    }) => {
      // Fetch SOV line details for each unique sov_line_id to get rates & descriptions
      const sovLineIds = [...new Set(input.assignments.map(a => a.sov_line_id))];
      const { data: sovLines, error: slErr } = await supabase
        .from("sov_lines")
        .select("id, description, sub_unit_price, unit, quantity")
        .in("id", sovLineIds);
      if (slErr) throw slErr;
      const slMap = new Map((sovLines ?? []).map((sl: any) => [sl.id, sl]));

      // Fetch scope item quantities for each assignment
      const scopeItemIds = [...new Set(input.assignments.map(a => a.unit_scope_item_id))];
      const { data: scopeItems, error: siErr } = await supabase
        .from("unit_scope_items")
        .select("id, quantity")
        .in("id", scopeItemIds);
      if (siErr) throw siErr;
      const siMap = new Map((scopeItems ?? []).map((si: any) => [si.id, si]));

      // Build work_order_lines rows
      const woLineRows = input.assignments.map(a => {
        const sl = slMap.get(a.sov_line_id);
        const si = siMap.get(a.unit_scope_item_id);
        const rate = sl?.sub_unit_price ?? 0;
        const qty = si?.quantity ?? 1;
        return {
          company_id: input.companyId,
          work_order_id: a.work_order_id,
          sov_line_id: a.sov_line_id,
          unit_id: a.unit_id,
          description: sl?.description ?? "—",
          quantity: qty,
          unit: sl?.unit ?? "SF",
          contract_rate: rate,
          line_total: qty * rate,
          status: "pending",
        };
      });

      const { error } = await supabase
        .from("work_order_lines")
        .insert(woLineRows as any);
      if (error) throw error;

      // Update WO total amounts
      const woIds = [...new Set(input.assignments.map(a => a.work_order_id))];
      for (const woId of woIds) {
        const { data: lines } = await supabase
          .from("work_order_lines")
          .select("line_total")
          .eq("work_order_id", woId)
          .not("status", "in", "(voided,cancelled)");
        const total = (lines ?? []).reduce((s: number, l: any) => s + Number(l.line_total ?? 0), 0);
        await supabase.from("work_orders").update({ amount: total } as any).eq("id", woId);
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["unit_assignments", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["unit_scope_items", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["wo_lines_for_units", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["work_orders", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["work_order_lines"] });
    },
  });
};

export const useUnitAssignmentDetails = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["unit_assignment_details", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_assignment_detail" as any)
        .select("*")
        .eq("project_id", projectId!);
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!projectId,
  });
};
